import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Badge, Button, Form, Spinner } from "react-bootstrap";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import ClickBack from "../../components/buttons/ClickBack";
import { fetchShifts, fetchAllLivreurs, deleteShift } from "./LivreursShiftsService";
import ShiftDetailsModal from "./ShiftDetailsModal";
import ConfirmationModal from "../../components/modal/ConfirmationModal";

function ListScreen() {
  const [shifts, setShifts] = useState([]);
  const [livreurs, setLivreurs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [pagination, setPagination] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    livreur_id: "",
    status: "",
    start_date: "",
    end_date: "",
  });

  // Modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState(null);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);

  // Current page
  const [currentPage, setCurrentPage] = useState(1);

  // User role
  const [userRole, setUserRole] = useState(null);

  // Fetch data
  const fetchData = async (page = 1) => {
    try {
      setLoading(true);
      const data = await fetchShifts(filters, page);
      setShifts(data.shifts);
      setStatistics(data.statistics);
      setPagination(data.pagination);
    } catch (error) {
      console.error("Error fetching shifts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch livreurs for dropdown
  const fetchLivreursData = async () => {
    try {
      const data = await fetchAllLivreurs();
      setLivreurs(data);
    } catch (error) {
      console.error("Error fetching livreurs:", error);
    }
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, filters]);

  useEffect(() => {
    fetchLivreursData();
    // Get user role from localStorage
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    setUserRole(Number(userData?.role));
  }, []);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      livreur_id: "",
      status: "",
      start_date: "",
      end_date: "",
    });
    setCurrentPage(1);
  };

  // Open details modal
  const handleViewDetails = (shiftId) => {
    setSelectedShiftId(shiftId);
    setShowDetailsModal(true);
  };

  // Close details modal
  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedShiftId(null);
  };

  // Open delete confirmation modal
  const handleDeleteClick = (shift) => {
    setShiftToDelete(shift);
    setShowDeleteModal(true);
  };

  // Confirm delete shift
  const handleConfirmDelete = async () => {
    try {
      await deleteShift(shiftToDelete.id);
      setShowDeleteModal(false);
      setShiftToDelete(null);
      // Refresh data after delete
      fetchData(currentPage);
    } catch (error) {
      console.error("Error deleting shift:", error);
      alert("Erreur lors de la suppression du shift");
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setShiftToDelete(null);
  };

  // Format duration
  const formatDuration = (hours) => {
    if (!hours) return "N/A";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge bg="success">En cours</Badge>;
      case "ended":
        return <Badge bg="secondary">Terminé</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  return (
    <Container className="mt-4">
      <Row className="align-items-center mb-4">
        <Col>
          <ClickBack />
          <h2 className="mb-0">Informations Livraisons</h2>
          <p className="text-muted">Historique des shifts des livreurs</p>
        </Col>
      </Row>

      {/* Statistics Cards */}
      {statistics && (
        <Row className="mb-4">
          <Col md={2}>
            <Card className="text-center">
              <Card.Body>
                <h3>{statistics.total_shifts}</h3>
                <small className="text-muted">Total shifts</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center" style={{ borderLeft: "4px solid #28a745" }}>
              <Card.Body>
                <h3>{statistics.active_shifts}</h3>
                <small className="text-muted">En cours</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center">
              <Card.Body>
                <h3>{statistics.ended_shifts}</h3>
                <small className="text-muted">Terminés</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center">
              <Card.Body>
                <h3>{statistics.total_distance_km}</h3>
                <small className="text-muted">Km parcourus</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center">
              <Card.Body>
                <h3>{statistics.total_deliveries}</h3>
                <small className="text-muted">Livraisons</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center">
              <Card.Body>
                <h3>{statistics.average_duration_hours}h</h3>
                <small className="text-muted">Durée moy.</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Livreur</Form.Label>
                <Form.Select
                  value={filters.livreur_id}
                  onChange={(e) => handleFilterChange("livreur_id", e.target.value)}
                >
                  <option value="">Tous les livreurs</option>
                  {livreurs.map((livreur) => (
                    <option key={livreur.id} value={livreur.id}>
                      {livreur.nom} {livreur.prenom}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label>Statut</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="">Tous</option>
                  <option value="active">En cours</option>
                  <option value="ended">Terminé</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Date début</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange("start_date", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Date fin</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange("end_date", e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col md={1} className="d-flex align-items-end">
              <Button variant="outline-secondary" onClick={handleResetFilters}>
                Réinitialiser
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Shifts Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Chargement...</span>
              </Spinner>
            </div>
          ) : shifts.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">Aucun shift trouvé</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Livreur</th>
                    <th>Début</th>
                    <th>Fin</th>
                    <th>Durée</th>
                    <th>Distance (km)</th>
                    <th>Livraisons</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shifts.map((shift) => (
                    <tr key={shift.id}>
                      <td>{shift.id}</td>
                      <td>
                        <div>
                          <strong>{shift.livreur_name}</strong>
                          {shift.livreur_phone && (
                            <div className="small text-muted">{shift.livreur_phone}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        {format(new Date(shift.start_time), "dd/MM/yyyy HH:mm", { locale: fr })}
                      </td>
                      <td>
                        {shift.end_time
                          ? format(new Date(shift.end_time), "dd/MM/yyyy HH:mm", { locale: fr })
                          : <Badge bg="warning">En cours</Badge>}
                      </td>
                      <td>{formatDuration(shift.duration_hours)}</td>
                      <td>{shift.total_distance_km || "N/A"}</td>
                      <td>{shift.total_deliveries || 0}</td>
                      <td>{getStatusBadge(shift.status)}</td>
                      <td>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleViewDetails(shift.id)}
                          className="me-2"
                        >
                          Détails
                        </Button>
                        {userRole === 5 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteClick(shift)}
                          >
                            Supprimer
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {pagination && pagination.last_page > 1 && (
            <Row className="mt-3">
              <Col className="d-flex justify-content-center">
                <Button
                  variant="outline-secondary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="me-2"
                >
                  Précédent
                </Button>
                <span className="align-self-center mx-3">
                  Page {currentPage} sur {pagination.last_page}
                </span>
                <Button
                  variant="outline-secondary"
                  disabled={currentPage === pagination.last_page}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="ms-2"
                >
                  Suivant
                </Button>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Shift Details Modal */}
      <ShiftDetailsModal
        show={showDetailsModal}
        onHide={handleCloseDetailsModal}
        shiftId={selectedShiftId}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        actionType="delete"
      />
    </Container>
  );
}

export default ListScreen;
